import PhoneNumberCodeInfo from "@/components/core/labels/PhoneNumberCodeInfo";
import { requestCreateContact } from "@/state/contact/ContactsEffects";
import { PhoneHelper } from "@/utils/helpers/phone-helper";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import SubmitButton from "@components/core/buttons/SubmitButton";
import InputFormField from "@components/core/inputs/InputFormField";
import { SelectFormField } from "@components/core/inputs/SelectFormField";
import { useContacts } from "@state/contact/ContactsHook";
import { Contact, Signatory } from "@type/core/dashboard/dashboard.types";
import {
  formHorizontalLayout,
  formRules,
  formValidateTriggers,
} from "@utils/Constant";
import { Checkbox, Form, FormInstance, type SelectProps } from "antd";
import { useForm } from "antd/lib/form/Form";
import {
  Dispatch,
  FunctionComponent,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export interface ContactFormValues {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  companyName: string;
  categories: string[];
}

interface ContactFormProps {
  isAddRecipient?: boolean;
  isAddSignatory?: boolean;
  isAddModelSignatory?: boolean;
  isAddModelRecipient?: boolean;
  isFromModel?: boolean;
  isEditContact?: boolean;
  setIsEmailAlreadyExistsModalVisible?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  signatories?: Signatory[];
  setSignatories?: (signatories: Signatory[]) => void;
  defaultContact?: Contact;
  officeId?: string;
  baseContacts?: Contact[];
  setIsContactAlreadyExistsModalVisible?: Dispatch<SetStateAction<boolean>>;
  setContactToAdd?: (contact?: Contact) => void;
  setExistingContact?: Dispatch<SetStateAction<Contact | undefined>>;
  showSubmitButton?: boolean;
  onSubmit?: (formValues: ContactFormValues) => void;
  formRef?: RefObject<FormInstance<ContactFormValues>>;
  signatoryError?: string;
}

const ContactForm: FunctionComponent<ContactFormProps> = ({
  isAddRecipient = false,
  isAddSignatory = false,
  isAddModelSignatory = false,
  isAddModelRecipient = false,
  isEditContact = false,
  isFromModel = false,
  setIsEmailAlreadyExistsModalVisible,
  className = "",
  signatories,
  setSignatories,
  defaultContact,
  officeId,
  baseContacts,
  setIsContactAlreadyExistsModalVisible,
  setContactToAdd,
  setExistingContact,
  showSubmitButton = true,
  onSubmit,
  formRef,
  signatoryError,
}) => {
  const { t } = useTranslation();
  const { contactForm } = useContacts();

  const categories = useMemo(() => {
    return defaultContact?.category && defaultContact.category.length > 0
      ? defaultContact.category?.split("|")
      : [];
  }, [defaultContact?.category]);

  const initialValues: ContactFormValues = {
    firstname: defaultContact?.firstname ?? "",
    lastname: defaultContact?.lastname ?? "",
    email: defaultContact?.email ?? "",
    phone: defaultContact?.phone ?? "",
    companyName: defaultContact?.companyName ?? "",
    categories,
  };

  const [form] = useForm();
  const [isAddContactChecked, setIsAddContactChecked] = useState<boolean>(
    isAddSignatory ||
      isAddRecipient ||
      isAddModelSignatory ||
      isAddModelRecipient,
  );
  const [submittable, setSubmittable] = useState(false);

  const selectOptions: SelectProps["options"] = [];
  contactForm?.category?.forEach((category) =>
    selectOptions.push({
      label: category.name,
      value: category.name,
    }),
  );

  const values = Form.useWatch([], form);

  useEffect(() => {
    if (
      form.getFieldValue("firstname") === "" ||
      form.getFieldValue("lastname") === "" ||
      form.getFieldValue("email") === "" ||
      (form.getFieldValue("phone") === "" && isAddSignatory) ||
      (form.getFieldValue("phone") === "" && isAddModelSignatory) ||
      (form.getFieldValue("phone") === "" &&
        !isAddSignatory &&
        !isAddRecipient &&
        !isAddModelSignatory &&
        !isAddModelRecipient) ||
      (form.getFieldValue("phone") === "" &&
        isAddRecipient &&
        isAddContactChecked) ||
      (form.getFieldValue("phone") === "" &&
        isAddModelRecipient &&
        isAddContactChecked)
    ) {
      setSubmittable(false);
    } else {
      setSubmittable(true);
    }
  }, [
    form,
    values,
    isAddRecipient,
    isAddSignatory,
    isAddModelSignatory,
    isAddModelRecipient,
    isAddContactChecked,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      firstname: defaultContact?.firstname ?? "",
      lastname: defaultContact?.lastname ?? "",
      email: defaultContact?.email ?? "",
      phone: defaultContact?.phone ?? "",
      companyName: defaultContact?.companyName ?? "",
      categories,
    });
  }, [defaultContact, categories, form]);

  const handleOnChangeCategory = (selectedCategories: string[]) => {
    form.setFieldValue("categories", selectedCategories);
  };

  const addContactToSignatoriesList = () => {
    if (!setSignatories) {
      return;
    }
    const newSignatory: Signatory = {
      key: form.getFieldValue("email") + Date.now().toString(),
      firstname: form.getFieldValue("firstname"),
      lastname: form.getFieldValue("lastname"),
      email: form.getFieldValue("email"),
      phone: form.getFieldValue("phone"),
      officeId: officeId,
    };
    setSignatories([...(signatories ?? []), newSignatory]);
  };

  const createContactInDatabase = (contact: Contact) => {
    requestCreateContact({ dto: contact })
      .then((response) => {
        if (response.ok) {
          toastSuccess(
            t("contacts.addSuccessfully", {
              firstname: contact.firstname,
              lastname: contact.lastname,
            }),
          );
        }
      })
      .catch(() => toastError(t("error.toast.contactCreate")));
  };

  const handleSubmit = () => {
    const formEmail = form.getFieldValue("email") as string;
    const formFirstname = form.getFieldValue("firstname") as string;
    const formLastname = form.getFieldValue("lastname") as string;
    const formPhone = form.getFieldValue("phone") as string;
    const formCompanyName = form.getFieldValue("companyName") as string;
    const formCategories = (form.getFieldValue("categories") as string[]) ?? [];
    const contact: Contact = {
      firstname: formFirstname,
      lastname: formLastname,
      phone: formPhone,
      companyName: formCompanyName,
      email: formEmail,
      officeId: officeId ? officeId : "",
    };
    const contactFormValues: ContactFormValues = {
      email: formEmail,
      firstname: formFirstname,
      lastname: formLastname,
      phone: PhoneHelper.removePhoneWhitespace(formPhone) ?? "",
      companyName: formCompanyName,
      categories: formCategories,
    };

    if (
      signatories?.filter(
        (signatory) => signatory.email === formEmail.toLowerCase(),
      ).length === 0
    ) {
      const existingContact = baseContacts?.find(
        (contact) =>
          contact.officeId === officeId &&
          contact.email.toLowerCase() === formEmail.toLowerCase(),
      );
      if (existingContact) {
        const isContactExactlyTheSame: boolean =
          existingContact.lastname.toLowerCase() ===
            formLastname.toLowerCase() &&
          existingContact.firstname.toLowerCase() ===
            formFirstname.toLowerCase() &&
          existingContact.phone === formPhone;
        if (isContactExactlyTheSame) {
          addContactToSignatoriesList();
        } else {
          setContactToAdd && setContactToAdd(contact);
          setExistingContact && setExistingContact(existingContact);
          setIsContactAlreadyExistsModalVisible &&
            setIsContactAlreadyExistsModalVisible(true);
          onSubmit?.(contactFormValues);
          // Dans ce cas on ne veut pas créer de signataire c'est pour ça qu'on return pour ne pas rentrer dans le "addContactToSignatoriesList"
          // pour pouvoir proposer la modal qui permet de choisir entre un contact de la db ou le nouveau contact rentré par l'utilisateur
          return;
        }
      }
      addContactToSignatoriesList();
    } else {
      setIsEmailAlreadyExistsModalVisible &&
        setIsEmailAlreadyExistsModalVisible(true);
    }
    if (isAddContactChecked) {
      createContactInDatabase(contact);
    }
    onSubmit?.(contactFormValues);
  };

  const isPhoneRequired =
    (isAddContactChecked && isAddRecipient) ||
    isAddSignatory ||
    (!isAddRecipient && !isAddSignatory);

  return (
    <>
      <Form
        ref={formRef}
        className={`app-form-contact flex-grow-1 d-flex flex-column ${className}`}
        form={form}
        onFinish={handleSubmit}
        initialValues={initialValues}
        {...formValidateTriggers}
        {...formHorizontalLayout}
      >
        <InputFormField
          showLabel
          module="contacts"
          field="firstname"
          required
          maxLength={45}
        />
        <InputFormField
          showLabel
          module="contacts"
          field="lastname"
          required
          maxLength={45}
        />
        <InputFormField
          showLabel
          module="contacts"
          field="email"
          rules={formRules.email(false)}
          required
          maxLength={150}
        />
        <InputFormField
          showLabel
          showTooltip
          labelTooltip="phone"
          module="contacts"
          field="phone"
          rules={formRules.phone(!isPhoneRequired)}
          required={isPhoneRequired}
          maxLength={45}
        />
        <PhoneNumberCodeInfo />
        <InputFormField showLabel module="contacts" field="companyName" />
        <SelectFormField
          fieldClassName="ant-select-multiple-custom"
          showLabel
          module="contacts"
          field={"category"}
          mode="tags"
          allowClear
          onChange={handleOnChangeCategory}
          options={contactForm?.category?.map((category) => ({
            label: category.name,
            value: category.name,
          }))}
        />
        {(isAddSignatory ||
          isAddRecipient ||
          isAddModelSignatory ||
          isAddModelRecipient) && (
          <Checkbox
            checked={isAddContactChecked}
            onChange={(e) => setIsAddContactChecked(e.target.checked)}
          >
            {t("contacts.add")}
          </Checkbox>
        )}
        {showSubmitButton && (
          <div className="flex-grow-1 d-flex align-items-end justify-content-center mt-32 mb-16">
            <SubmitButton
              disabled={!submittable}
              label={isEditContact ? t("buttons.edit") : t("buttons.validate")}
              className="mw-160"
            />
          </div>
        )}
      </Form>
    </>
  );
};

export default ContactForm;
